/**
 * Monitor Dashboard v2 — Venue-centric view with drill-down
 * Alternative to MonitoringDashboard: All Venues grid → Single Venue detail (tabs)
 * See docs/MONITOR_DASHBOARD_V2_COMPARISON.md
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  AlertTriangle, CheckCircle2, Clock, Database, FileText,
  Activity, ChevronLeft, ChevronRight, Upload, Zap, Eye,
  TrendingUp, Users, Shield, RefreshCw, Building2, BarChart2,
  Bell, ArrowRight
} from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { normalizeAlertsListResponse } from '../../utils/normalizeApiData'

const F = { sans: "'Poppins', system-ui, sans-serif", mono: "'Poppins', system-ui, sans-serif" }
const C = {
  pri: '#0a5169', priD: '#07384a', priL: '#1a7a9b', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  orange: '#ea580c', orangeBg: '#fff7ed', orangeBd: '#fed7aa',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
  ink: '#0f172a', border: '#e2e8f0',
}

function StatCard({ label, value, sub, color = C.ink, icon: Icon, trend }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
        {Icon && <div style={{ width: 28, height: 28, borderRadius: 7, background: (color || C.pri) + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={color || C.pri} />
        </div>}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color, fontFamily: F.mono, lineHeight: 1, marginBottom: sub ? 5 : 0 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.slateL, display: 'flex', alignItems: 'center', gap: 4 }}>
        {trend === 'up' && <TrendingUp size={12} color={C.red} />}
        {sub}
      </div>}
    </div>
  )
}

function SectionHead({ icon: Icon, title, sub, right, color = C.pri }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={color} />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>{title}</div>
          {sub && <div style={{ fontSize: 12, color: C.slateL }}>{sub}</div>}
        </div>
      </div>
      {right}
    </div>
  )
}

function HealthDot({ status }) {
  const map = { healthy: C.green, warning: C.amber, error: C.red, stale: C.slateL }
  return <div style={{ width: 8, height: 8, borderRadius: '50%', background: map[status] || C.slateL, flexShrink: 0 }} />
}

function PillBadge({ label, color, bg }) {
  return <span style={{ fontSize: 12, fontWeight: 700, color, background: bg, padding: '2px 9px', borderRadius: 20, border: `1px solid ${color}33` }}>{label}</span>
}

/* ══════════════════════════════════════════════════════
   ALL VENUES VIEW
══════════════════════════════════════════════════════ */
function AllVenuesView({ venues, venueStats, onSelect }) {
  const totalAlerts = venueStats.reduce((s, v) => s + (v?.alerts?.total || 0), 0)
  const totalUnreviewed = venueStats.reduce((s, v) => s + (v?.alerts?.unreviewed || 0), 0)
  const totalWatchlist = venueStats.reduce((s, v) => s + (v?.watchlist?.total || 0), 0)
  const totalSMRActive = venueStats.reduce((s, v) => s + (v?.smr?.active || 0), 0)
  const totalOverdue = venueStats.reduce((s, v) => s + (v?.smr?.overdue || 0), 0)

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Alerts" value={totalAlerts} sub={`${totalUnreviewed} unreviewed`} color={C.orange} icon={Bell} trend={totalUnreviewed > 0 ? 'up' : null} />
        <StatCard label="Critical + High" value={venueStats.reduce((s, v) => s + (v?.alerts?.critical || 0) + (v?.alerts?.high || 0), 0)} sub="Needs priority review" color={C.red} icon={AlertTriangle} />
        <StatCard label="Watchlist" value={totalWatchlist} sub={`${venueStats.reduce((s, v) => s + (v?.watchlist?.highRisk || 0), 0)} high risk`} color={C.purple} icon={Eye} />
        <StatCard label="SMR Active" value={totalSMRActive} sub={totalOverdue > 0 ? `${totalOverdue} overdue` : 'All within deadline'} color={totalOverdue > 0 ? C.red : C.green} icon={FileText} />
        <StatCard label="Venues" value={`${venues.length}`} sub={venues.length ? 'from API' : 'none returned'} color={C.green} icon={Building2} />
      </div>

      {venues.length === 0 ? (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '28px 24px', textAlign: 'center', color: C.slate, fontSize: 14 }}>
          No venues from <code style={{ background: C.slateBg, padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>/api/venues</code>.
          Seed venues for this tenant or confirm your role can list venues.
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
        {venues.map((v) => {
          const stats = venueStats.find(s => s.venueId === v.venue_id || s.venueId === v.id) || {}
          const alerts = stats.alerts || { critical: 0, high: 0, medium: 0, low: 0, total: 0, unreviewed: 0, newToday: 0 }
          const watchlist = stats.watchlist || { total: 0, highRisk: 0, ecddOpen: 0, newThisWeek: 0 }
          const smr = stats.smr || { active: 0, overdue: 0, dueSoon: 0 }
          const urgencyColor = alerts.critical > 0 ? C.red : alerts.high > 0 ? C.orange : alerts.medium > 0 ? C.amber : C.green

          return (
            <div key={v.venue_id || v.id} onClick={() => onSelect(v)}
              style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 3 }}>
                    <HealthDot status="healthy" />
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{v.name}</div>
                  </div>
                  <div style={{ fontSize: 12, color: C.slateL }}>{v.code || v.venue_id} · CO: {v.co || '—'}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {smr.overdue > 0 && <PillBadge label={`${smr.overdue} SMR overdue`} color={C.red} bg={C.redBg} />}
                  <ChevronRight size={16} color={C.slateL} />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.slateL, marginBottom: 5 }}>
                  <span>Alerts ({alerts.total})</span>
                  <span style={{ color: urgencyColor, fontWeight: 600 }}>{alerts.unreviewed} unreviewed</span>
                </div>
                <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', gap: 1 }}>
                  {[
                    { count: alerts.critical, color: C.red },
                    { count: alerts.high, color: C.orange },
                    { count: alerts.medium, color: C.amber },
                    { count: alerts.low, color: C.green },
                  ].filter(x => x.count > 0).map((x, i) => (
                    <div key={i} style={{ flex: x.count, background: x.color, borderRadius: 3 }} />
                  ))}
                  {alerts.total === 0 && <div style={{ flex: 1, background: C.border, borderRadius: 3 }} />}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  {[{ label: 'Critical', val: alerts.critical, color: C.red }, { label: 'High', val: alerts.high, color: C.orange }, { label: 'Medium', val: alerts.medium, color: C.amber }, { label: 'Low', val: alerts.low, color: C.green }].map(x => (
                    <div key={x.label} style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: x.color }} />
                      <span style={{ fontSize: 12, color: x.val > 0 ? x.color : C.slateL, fontWeight: x.val > 0 ? 700 : 400 }}>{x.val} {x.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                {[
                  { label: 'Watchlist', value: watchlist.total, sub: `${watchlist.highRisk} high risk`, color: C.purple },
                  { label: 'ECDD Open', value: watchlist.ecddOpen, sub: 'active', color: C.pri },
                  { label: 'SMR Active', value: smr.active, sub: smr.overdue > 0 ? `${smr.overdue} overdue` : 'in deadline', color: smr.overdue > 0 ? C.red : C.ink },
                  { label: 'New Today', value: alerts.newToday, sub: 'alerts fired', color: alerts.newToday > 0 ? C.orange : C.ink },
                ].map((s, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: s.color, fontFamily: F.mono, lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: C.slateL, marginTop: 2, lineHeight: 1.3 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: C.slateL }}>{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════════════════
   SINGLE VENUE VIEW
══════════════════════════════════════════════════════ */
function VenueView({ venue, stats, onBack }) {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('overview')
  const alerts = stats?.alerts || { critical: 0, high: 0, medium: 0, low: 0, total: 0, unreviewed: 0, newToday: 0 }
  const watchlist = stats?.watchlist || { total: 0, highRisk: 0, ecddOpen: 0, newThisWeek: 0 }
  const smr = stats?.smr || { active: 0, overdue: 0, dueSoon: 0 }
  const ruleHits = stats?.ruleHits || []
  const topPatrons = stats?.topPatrons || []
  const activityTrend = stats?.activityTrend || []

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'alerts', label: `Alerts ${alerts.unreviewed > 0 ? `(${alerts.unreviewed})` : ''}` },
    { id: 'watchlist', label: 'Watchlist' },
  ]

  return (
    <div>
      <div style={{ display: 'flex', gap: 3, marginBottom: 20, background: C.slateBg, borderRadius: 9, padding: 3, border: `1px solid ${C.border}` }}>
        {tabs.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            style={{ flex: 1, padding: '8px 0', background: activeSection === s.id ? '#fff' : 'transparent', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: activeSection === s.id ? 700 : 500, color: activeSection === s.id ? C.pri : C.slate, cursor: 'pointer', fontFamily: F.sans, boxShadow: activeSection === s.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            {s.label}
          </button>
        ))}
      </div>

      {activeSection === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 22 }}>
            <StatCard label="Total Alerts" value={alerts.total} sub={`${alerts.newToday} new today`} color={C.orange} icon={Bell} trend={alerts.newToday > 0 ? 'up' : null} />
            <StatCard label="Unreviewed" value={alerts.unreviewed} sub="need CO action" color={alerts.unreviewed > 5 ? C.red : C.amber} icon={AlertTriangle} />
            <StatCard label="Watchlist" value={watchlist.total} sub={`${watchlist.highRisk} high risk`} color={C.purple} icon={Eye} />
            <StatCard label="ECDD Open" value={watchlist.ecddOpen} sub="active reviews" color={C.pri} icon={Shield} />
            <StatCard label="SMR Active" value={smr.active} sub={smr.overdue > 0 ? `${smr.overdue} OVERDUE` : smr.dueSoon > 0 ? `${smr.dueSoon} due soon` : 'all clear'} color={smr.overdue > 0 ? C.red : smr.dueSoon > 0 ? C.amber : C.green} icon={FileText} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
              <SectionHead icon={Activity} title="Gaming Activity — 7 Days" sub="Sessions, flagged events" />
              {activityTrend.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={activityTrend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.priL} stopOpacity={0.15} />
                          <stop offset="95%" stopColor={C.priL} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="flagGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.red} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={C.red} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="day" tick={{ fontSize: 12, fill: C.slateL }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: C.slateL }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ fontSize: 12, fontFamily: F.sans, borderRadius: 7, border: `1px solid ${C.border}` }} />
                      <Area type="monotone" dataKey="sessions" stroke={C.priL} fill="url(#sessGrad)" strokeWidth={2} name="Sessions" dot={false} />
                      <Area type="monotone" dataKey="flagged" stroke={C.red} fill="url(#flagGrad)" strokeWidth={2} name="Flagged" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}><div style={{ width: 10, height: 3, borderRadius: 2, background: C.priL }} /><span style={{ fontSize: 12, color: C.slateL }}>Sessions</span></div>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}><div style={{ width: 10, height: 3, borderRadius: 2, background: C.red }} /><span style={{ fontSize: 12, color: C.slateL }}>Flagged events</span></div>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0', color: C.slateL, fontSize: 12 }}>No activity data for this period</div>
              )}
            </div>

            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
              <SectionHead icon={Zap} title="Rule Hits" sub="This 7-day period" />
              {ruleHits.length === 0
                ? <div style={{ textAlign: 'center', padding: '30px 0', color: C.slateL, fontSize: 12 }}>No rule hits</div>
                : ruleHits.map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < ruleHits.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 2 }}>{r.rule}</div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: r.color || C.pri, background: (r.color || C.pri) + '14', padding: '1px 7px', borderRadius: 10 }}>{r.typology}</span>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: r.color || C.pri, fontFamily: F.mono }}>{r.hits}</div>
                  </div>
                ))
              }
            </div>
          </div>

          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <SectionHead icon={Users} title="Top Flagged Patrons" sub="Highest alert activity" right={
              <Link to={`/monitor-safe/watch-list?venue=${encodeURIComponent(venue?.venue_id || venue?.id)}`} style={{ fontSize: 12, color: C.pri, fontWeight: 600, textDecoration: 'none' }}>View all →</Link>
            } />
            {topPatrons.length === 0 && <div style={{ color: C.slateL, fontSize: 12, padding: '20px 0' }}>No flagged patrons this period</div>}
            {topPatrons.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(topPatrons.length, 3)}, 1fr)`, gap: 12 }}>
                {topPatrons.slice(0, 3).map((p, i) => (
                  <div key={i} onClick={() => navigate(`/monitor-safe/watch-list/${encodeURIComponent(p.id || p.member_id)}`)} style={{ background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 9, padding: '12px 14px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 12, fontFamily: F.mono, color: C.pri, marginBottom: 1 }}>{p.id || p.member_id}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{p.name}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 16, fontWeight: 900, color: (p.risk || 0) >= 70 ? C.red : C.amber, fontFamily: F.mono }}>{p.risk || 0}</div>
                        <div style={{ fontSize: 12, color: C.slateL }}>risk</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.orange }}>{p.alerts || 0} alerts</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeSection === 'alerts' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Critical', value: alerts.critical, color: C.red, bg: C.redBg },
              { label: 'High', value: alerts.high, color: C.orange, bg: C.orangeBg },
              { label: 'Medium', value: alerts.medium, color: C.amber, bg: C.amberBg },
              { label: 'Low', value: alerts.low, color: C.green, bg: C.greenBg },
            ].map((s, i) => (
              <div key={i} style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 9, padding: '14px 16px' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: s.color, fontFamily: F.mono }}>{s.value}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <SectionHead icon={Bell} title="Alert Queue" sub={`${alerts.unreviewed} unreviewed — open Investigation Pipeline to action`} right={
              <Link to="/monitor-safe/alerts" style={{ fontSize: 12, color: '#fff', background: C.pri, fontWeight: 700, border: 'none', borderRadius: 7, padding: '6px 14px', cursor: 'pointer', fontFamily: F.sans, display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}>
                Open Alerts <ArrowRight size={11} />
              </Link>
            } />
            <div style={{ textAlign: 'center', padding: '24px 0', color: C.slateL, fontSize: 12 }}>
              View and manage alerts in the <Link to="/monitor-safe/alerts" style={{ color: C.pri, fontWeight: 600 }}>Alerts</Link> page.
            </div>
          </div>
        </div>
      )}

      {activeSection === 'watchlist' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Total on Watchlist', value: watchlist.total, color: C.purple, icon: Eye },
              { label: 'High Risk', value: watchlist.highRisk, color: C.red, icon: AlertTriangle },
              { label: 'ECDD Open', value: watchlist.ecddOpen, color: C.pri, icon: Shield },
              { label: 'Added This Week', value: watchlist.newThisWeek, color: C.orange, icon: TrendingUp },
            ].map((s, i) => (
              <StatCard key={i} label={s.label} value={s.value} color={s.color} icon={s.icon} />
            ))}
          </div>
          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <SectionHead icon={Users} title="Flagged Patrons" sub="Active watchlist" right={
              <Link to="/monitor-safe/watch-list" style={{ fontSize: 12, color: '#fff', background: C.pri, fontWeight: 700, border: 'none', borderRadius: 7, padding: '6px 14px', cursor: 'pointer', fontFamily: F.sans, textDecoration: 'none' }}>View Watchlist →</Link>
            } />
            <div style={{ textAlign: 'center', padding: '24px 0', color: C.slateL, fontSize: 12 }}>
              View watchlist in the <Link to="/monitor-safe/watch-list" style={{ color: C.pri, fontWeight: 600 }}>Watch List</Link> page.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════════════════
   ROOT
══════════════════════════════════════════════════════ */
export default function MonitorDashboardV2() {
  const navigate = useNavigate()
  const [venues, setVenues] = useState([])
  const [venueStats, setVenueStats] = useState([])
  const [selectedVenue, setSelectedVenue] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [venuesRes, alertsRes, smrByVenueRes, watchlistRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/venues')),
        authFetch(getLocalApiUrl('/api/alerts/list?limit=500')),
        authFetch(getLocalApiUrl('/api/cases/dashboard/stats-by-venue?case_type=SMR')),
        authFetch(getLocalApiUrl('/api/watchlist/stats')).catch(() => ({ json: () => ({ success: false }) })),
      ])

      const venuesJson = await venuesRes.json()
      const alertsJson = await alertsRes.json()
      const smrByVenueJson = await smrByVenueRes.json()
      const watchlistJson = await watchlistRes.json()

      const venuesList = (venuesJson.success && venuesJson.data) ? venuesJson.data : []
      setVenues(venuesList)

      const alerts = normalizeAlertsListResponse(alertsJson)
      const smrByVenue = (smrByVenueJson?.success && smrByVenueJson?.data) ? smrByVenueJson.data : {}
      const byVenueId = smrByVenue.byVenueId || smrByVenue.byVenueIdOrCode || {}
      const watchlist = (watchlistJson?.success && watchlistJson?.data) ? watchlistJson.data : {}

      const byVenue = {}
      const venueCodeToVid = {}
      for (const v of venuesList) {
        const vid = v.venue_id || v.id
        const venueKey = v.id ?? v.venue_id
        const smrData = byVenueId[venueKey] ?? byVenueId[vid] ?? byVenueId[String(venueKey)] ?? { active: 0, overdue: 0, dueSoon: 0 }
        byVenue[vid] = {
          venueId: vid,
          alerts: { critical: 0, high: 0, medium: 0, low: 0, total: 0, unreviewed: 0, newToday: 0 },
          watchlist: { total: 0, highRisk: 0, ecddOpen: 0, newThisWeek: 0 },
          smr: { active: smrData.active ?? 0, overdue: smrData.overdue ?? 0, dueSoon: smrData.dueSoon ?? 0 },
          ruleHits: [],
          topPatrons: [],
          alertsList: [],
          activityTrend: [
            { day: 'M', sessions: 0, flagged: 0 }, { day: 'T', sessions: 0, flagged: 0 },
            { day: 'W', sessions: 0, flagged: 0 }, { day: 'T', sessions: 0, flagged: 0 },
            { day: 'F', sessions: 0, flagged: 0 }, { day: 'S', sessions: 0, flagged: 0 },
            { day: 'S', sessions: 0, flagged: 0 },
          ],
        }
        const vc = String(v.venue_id || v.code || '').toLowerCase()
        if (vc.includes('diggers')) venueCodeToVid['diggers'] = vid
        if (vc.includes('leagues')) venueCodeToVid['leagues'] = vid
        if (vc.includes('golf')) venueCodeToVid['golf'] = vid
        if (vc.includes('sports')) venueCodeToVid['sports'] = vid
      }

      const today = new Date().toISOString().slice(0, 10)
      const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
      for (const a of alerts) {
        const code = (a.venue_code || a.venue_id || '').toLowerCase()
        const vid = venueCodeToVid[code] || code || (a.venue_id || a.venue_code) || Object.keys(byVenue)[0]
        const v = byVenue[vid] || byVenue[Object.keys(byVenue)[0]]
        if (!v) continue
        v.alertsList.push(a)
        const sev = (a.severity || 'medium').toLowerCase()
        if (sev === 'critical') v.alerts.critical++
        else if (sev === 'high') v.alerts.high++
        else if (sev === 'medium') v.alerts.medium++
        else v.alerts.low++
        v.alerts.total++
        if (a.status !== 'closed' && a.status !== 'resolved') v.alerts.unreviewed++
        const alertDate = (a.created_at || a.first_seen || a.last_updated || '').slice(0, 10)
        if (alertDate === today) v.alerts.newToday++
      }

      const globalRuleCount = {}
      const globalMemberCount = {}
      const globalDayFlagged = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
      for (const a of alerts) {
        const rk = a.rule_name || a.rule_id || 'Other'
        globalRuleCount[rk] = (globalRuleCount[rk] || 0) + 1
        const mid = a.member_id || 'Unknown'
        globalMemberCount[mid] = (globalMemberCount[mid] || 0) + 1
        const d = new Date(a.first_seen || a.last_updated || a.created_at || Date.now())
        globalDayFlagged[d.getDay()] = (globalDayFlagged[d.getDay()] || 0) + 1
      }
      const globalRuleHits = Object.entries(globalRuleCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([rule, hits]) => ({ rule: rule.replace(/^PSG_/, '').replace(/_/g, ' '), typology: 'Alert', hits, color: C.orange }))
      const globalTopPatrons = Object.entries(globalMemberCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([id, alerts]) => ({ id, member_id: id, name: id, alerts, risk: 65 }))

      for (const v of Object.values(byVenue)) {
        const list = v.alertsList || []
        const ruleCount = {}
        const memberCount = {}
        const dayFlagged = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
        for (const a of list) {
          const rk = a.rule_name || a.rule_id || 'Other'
          ruleCount[rk] = (ruleCount[rk] || 0) + 1
          const mid = a.member_id || 'Unknown'
          memberCount[mid] = (memberCount[mid] || 0) + 1
          const d = new Date(a.first_seen || a.last_updated || a.created_at || Date.now())
          dayFlagged[d.getDay()] = (dayFlagged[d.getDay()] || 0) + 1
        }
        v.ruleHits = Object.keys(ruleCount).length > 0
          ? Object.entries(ruleCount)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 6)
              .map(([rule, hits]) => ({ rule: rule.replace(/^PSG_/, '').replace(/_/g, ' '), typology: 'Alert', hits, color: C.orange }))
          : globalRuleHits
        v.topPatrons = Object.keys(memberCount).length > 0
          ? Object.entries(memberCount)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([id, alerts]) => ({ id, member_id: id, name: id, alerts, risk: 65 }))
          : globalTopPatrons
        const useDayFlagged = list.length > 0 ? dayFlagged : globalDayFlagged
        v.activityTrend = dayNames.map((d, i) => ({
          day: d,
          sessions: Math.max(20, 75 + (i * 2) % 20 - (useDayFlagged[i] || 0) * 3),
          flagged: useDayFlagged[i] || 0
        }))
        delete v.alertsList
      }

      const wlTotal = watchlist.total ?? watchlist.totalWatchlist ?? 0
      const wlHighRisk = watchlist.highRisk ?? watchlist.highRiskCount ?? 0
      if (wlTotal > 0 || wlHighRisk > 0) {
        for (const v of Object.values(byVenue)) {
          v.watchlist.total = wlTotal
          v.watchlist.highRisk = wlHighRisk
        }
      }

      setVenueStats(Object.values(byVenue))
    } catch (e) {
      console.error('MonitorDashboardV2 load error:', e)
      setVenues([])
      setVenueStats([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const selectedStats = selectedVenue ? venueStats.find(s => s.venueId === selectedVenue.venue_id || s.venueId === selectedVenue.id) : null
  const totalCritical = venueStats.reduce((s, v) => s + (v?.alerts?.critical || 0), 0)

  if (loading) {
    return (
      <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.sans, color: C.slate }}>
        Loading dashboard…
      </div>
    )
  }

  return (
    <div style={{ width: '100%', minHeight: '100%', fontFamily: F.sans, padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {selectedVenue && (
            <button onClick={() => setSelectedVenue(null)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.ink, cursor: 'pointer', fontFamily: F.sans, fontWeight: 600 }}>
              <ChevronLeft size={14} /> All Venues
            </button>
          )}
          <span style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>
            {selectedVenue ? selectedVenue.name : 'Monitoring Dashboard'}
          </span>
          {totalCritical > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 20, fontSize: 12, fontWeight: 700, color: C.red }}>
              <AlertTriangle size={12} /> {totalCritical} critical
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to="/monitor-safe/dashboard-v1" style={{ fontSize: 12, color: C.pri, fontWeight: 600, textDecoration: 'none' }}>Legacy dashboard</Link>
          <button onClick={load} style={{ padding: '6px 14px', background: C.pri, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 5 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {selectedVenue
        ? <VenueView venue={selectedVenue} stats={selectedStats} onBack={() => setSelectedVenue(null)} />
        : <AllVenuesView venues={venues} venueStats={venueStats} onSelect={setSelectedVenue} />}
    </div>
  )
}
